import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import {
    HomeIcon,
    PhotoIcon,
    UserGroupIcon,
    DocumentArrowDownIcon,
    BuildingStorefrontIcon,
    ShoppingBagIcon,
    ClipboardDocumentListIcon,
    HeartIcon,
    UserCircleIcon,
    BriefcaseIcon,
    EyeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { errorHandler } from "../../utils/HelperFunctions";
import { AxiosError } from "axios";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";

// Reusable Setting Card
const SettingCard = ({
    icon: Icon,
    label,
    to,
    count,
    todayUsers,
    showcount = true
}: {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    label: string;
    to: string;
    count: number;
    todayUsers: number;
    showcount: boolean;
}) => {
    const navigate = useNavigate();

    return (
        <div
            className="col-span-12 lg:col-span-3 cursor-pointer"
            onClick={() =>
                navigate(to, {
                    state: {
                        title: label,
                    },
                })
            }
        >
            <div className="card h-full">
                <Card className="h-full hover:shadow-lg transition-all">

                    <div className={`flex flex-col h-full ${showcount ? "justify-between" : "justify-center"}`}>

                        {/* Top / Center: Icon + Title */}
                        <div className={`flex items-center gap-3 ${showcount ? "mb-3" : "justify-center text-center flex-col gap-2"}`}>

                            <div className="p-2 rounded-lg bg-primary/10">
                                <Icon className="w-6 h-6 text-primary" />
                            </div>

                            <h3 className="font-semibold text-gray-800 text-sm">
                                {label}
                            </h3>

                        </div>

                        {/* Middle: Count */}
                        {showcount && (
                            <div className="flex items-center gap-3">

                                <span className="text-2xl font-bold text-gray-700">
                                    {count}
                                </span>

                                <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-3 py-2 rounded-full">
                                    + {todayUsers}
                                </span>

                            </div>
                        )}

                        {/* Bottom Arrow only when count */}
                        {showcount ? (
                            <div className="flex justify-end mt-3">
                                <ArrowRightIcon className="w-5 h-5 text-primary transition" />
                            </div>
                        ) : (
                            <div className="flex justify-center mt-3">
                                <ArrowRightIcon className="w-5 h-5 text-primary transition" />
                            </div>
                        )}

                    </div>

                </Card>
            </div>
        </div>
    );
};

const ActiveUsersCards: React.FC = () => {
    const settingsData = [

        {
            icon: HomeIcon,
            label: "Home Screen - Post",
            to: "/admin/activeusers/homepost",
            name: "homepost"
        },

        {
            icon: PhotoIcon,
            label: "Home Screen - Story",
            to: "/admin/activeusers/homestoryseen",
            name: "homestoryseen"
        },

        {
            icon: UserGroupIcon,
            label: "Matrimonial List",
            to: "/admin/activeusers/maitrimoniallist",
            name: "maitrimoniallist"
        },

        {
            icon: EyeIcon,
            label: "Matrimonial Details",
            to: "/admin/activeusers/showmaitrimonialdetails",
            name: "showmaitrimonialdetails"
        },

        {
            icon: DocumentArrowDownIcon,
            label: "Matrimonial PDF Download",
            to: "/admin/activeusers/maitrimonialdownload",
            name: "maitrimonialdownload"
        },

        {
            icon: BuildingStorefrontIcon,
            label: "Business Page",
            to: "/admin/activeusers/businesspage",
            name: "businesspage"
        },

        {
            icon: EyeIcon,
            label: "Business Details",
            to: "/admin/activeusers/showbusiness",
            name: "showbusiness"
        },

        {
            icon: ShoppingBagIcon,
            label: "Product Page",
            to: "/admin/activeusers/productpage",
            name: "productpage"
        },

        {
            icon: ClipboardDocumentListIcon,
            label: "Product Details",
            to: "/admin/activeusers/productdetails",
            name: "productdetails"
        },

        {
            icon: ClipboardDocumentListIcon,
            label: "Bazar Bhav",
            to: "/admin/activeusers/showbazarbhavdetails",
            name: "showbazarbhavdetails"
        },

        {
            icon: HeartIcon,
            label: "Blood Group List",
            to: "/admin/activeusers/showbloodgrouplist",
            name: "showbloodgrouplist"
        },

        {
            icon: UserCircleIcon,
            label: "User Profile Details",
            to: "/admin/activeusers/showuserdetails",
            name: "showuserdetails"
        },

        {
            icon: BriefcaseIcon,
            label: "Job Page",
            to: "/admin/activeusers/showjobs",
            name: "showjobs"
        },

        {
            icon: EyeIcon,
            label: "Job Details",
            to: "/admin/activeusers/showjobdetails",
            name: "showjobdetails",
            showcount: true
        },

    ];

    const initialized = useRef(false);
    const { showToast }: any = useToast();
    const [activeUsers, setActiveUsers] = useState<any[]>([]);

    async function getNewLeadsData() {
        try {
            const response = await apiFunctions.get("/activeusers/screen-count");

            if (response.data.success) {
                setActiveUsers(response.data.data);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        }
    }

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const screenCountMap = React.useMemo(() => {
        const map: Record<string, { total: number; today: number }> = {};

        activeUsers.forEach((item) => {
            if (item?.screen) {
                map[item.screen] = {
                    total: item.totalUsers || 0,
                    today: item.todayUsers || 0
                };
            }
        });

        return map;
    }, [activeUsers]);

    return (
        <div className="dashboard-margin">
            {/* Grid Layout Like Dashboard Cards */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                {settingsData.map((setting, index) => {

                    const countData = screenCountMap[setting.name] || { total: 0, today: 0 };

                    return (
                        <SettingCard
                            key={index}
                            icon={setting.icon}
                            label={setting.label}
                            to={setting.to}
                            count={countData.total}
                            todayUsers={countData.today}
                            showcount={setting.showcount !== false}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default ActiveUsersCards;